<script>
  // Read-only Winston log viewer over the shared "logs" mongo collection
  // (written by telebroad-integrations). Matches the filter set + display
  // shape of integrate.telebroad.com/logs but reuses hook-index's M3 stack.
  import { onMount } from 'svelte';
  import hljs from 'highlight.js/lib/core';
  import json from 'highlight.js/lib/languages/json';
  import { api } from '../lib/api.js';
  import { ui } from '../lib/ui.svelte.js';
  import TextField from '../components/TextField.svelte';
  import DateField from '../components/DateField.svelte';
  import MultiSelect from '../components/MultiSelect.svelte';

  // Register only the json grammar -- everything we render is mongo bson
  // serialised to JSON, and the json bundle alone is < 5KB gzipped.
  hljs.registerLanguage('json', json);

  // --- Filter state -----------------------------------------------------
  // URL query params seed initial state so links like ?from=...&to=... open
  // straight to the right window, matching the legacy /logs?from=&to= URLs.
  function readUrlSeed() {
    const u = new URLSearchParams(window.location.search);
    return {
      from: u.get('from') || defaultFrom(),
      to:   u.get('to')   || defaultTo(),
      levels: csv(u.get('levels')),
      hostnames: csv(u.get('hostnames')),
      applications: csv(u.get('applications')),
      sections: csv(u.get('sections')),
      versions: csv(u.get('versions')),
      log_types: csv(u.get('log_types')),
      customer_id: u.get('customer_id') || '',
      people_id:   u.get('people_id')   || '',
      phone_id:    u.get('phone_id')    || '',
      call_id:     u.get('call_id')     || '',
      transaction_id: u.get('transaction_id') || '',
      path: u.get('path') || '',
      log_id: u.get('log_id') || '',
    };
  }
  function csv(v) { return v ? v.split(',').filter(Boolean) : []; }
  function defaultFrom() {
    const d = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return toLocalIso(d);
  }
  // Open by default: an empty `to` means "up to now", so the upper bound is
  // never frozen into the URL and every refresh/return queries the latest
  // logs. The user can still set an explicit end time for a bounded window.
  function defaultTo() { return ''; }
  function toLocalIso(d) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  let filter = $state(readUrlSeed());
  let logs = $state([]);
  let total = $state(0);
  let totalExact = $state(true);
  // Pagination cursor: the _id of the oldest row on screen. Paging asks for
  // rows older than it (before_id) instead of skipping a growing offset --
  // skip makes the server walk and discard everything it skips, so page N
  // costs O(N*limit) and deep paging crawls.
  let cursor = $state('');
  let limit = $state(40);
  // Set once the server returns a short page (fewer rows than `limit`). That is
  // the only end-of-data signal available: the server deliberately does not
  // count matches (too slow), so nothing it returns says "this is the last
  // page". Without this flag every "is there more?" test stays true forever.
  let atEnd = $state(false);
  let loading = $state(false);
  let err = $state('');
  let expanded = $state(new Set()); // _id values currently expanded
  let selected = $state(new Set()); // _id values currently checked
  let lastSelIndex = -1;            // anchor row for shift-click range select
  let filtersOpen = $state(true);   // filter card collapsed/expanded (gives the table the page)

  // Infinite-scroll sentinel: when this element scrolls into view we kick off
  // the next page. Bound below the list; only used when there's more to load.
  let sentinel = $state(null);

  // Distinct value caches for the multi-select chips.
  let opts = $state({ level: [], hostname: [], applicationName: [], section: [], version: [], 'meta.logType': [] });

  // Versions and hostnames look better newest-first: semver-aware compare for
  // versions (so "1.10.0" beats "1.2.0"), natural compare for hostnames (so
  // pod suffixes group correctly). Everything else stays alphabetical asc.
  const cmpNatural = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }).compare;
  function sortValues(field, values) {
    if (field === 'version' || field === 'hostname') {
      return values.slice().sort((a, b) => cmpNatural(b, a));
    }
    return values.slice().sort();
  }

  // AbortControllers for the three categories of in-flight request. Reset
  // (or any new fetch) calls abort on the previous one so the old fetch is
  // cancelled by the browser; the Go server's r.Context() goes Done in turn,
  // which the mongo / mariadb drivers respect -- the cancel propagates
  // all the way to the database and returns context.Canceled.
  let logsCtl = null;
  let distinctCtl = null;
  let directoryCtl = null;
  function isAbort(e) {
    return e?.name === 'AbortError' || /aborted|signal is aborted/i.test(String(e?.message || ''));
  }

  // Distinct refresh is debounced because every multi-select tick would
  // otherwise spam the backend during rapid clicking. The server uses an
  // aggregation pipeline with a $limit(5000) on the most recent matching
  // docs, so even narrowing by a high-volume app stays bounded.
  let distinctTimer = null;
  // The auto-apply watchers below run once when the component mounts. onMount
  // already does the first load, so they stay quiet until it has happened --
  // deliberately a plain let, not $state, so flipping it doesn't re-run them.
  let started = false;
  let distinctSeq = 0;
  let distinctLoading = $state(false);
  async function loadDistinct() {
    const fields = ['level', 'hostname', 'applicationName', 'section', 'version', 'meta.logType'];
    distinctCtl?.abort();
    distinctCtl = new AbortController();
    const seq = ++distinctSeq;
    const f = { ...filter };
    distinctLoading = true;
    try {
      const results = await Promise.allSettled(
        fields.map((field) => api.logDistinct(field, f, { signal: distinctCtl.signal })),
      );
      if (seq !== distinctSeq) return;
      for (let i = 0; i < fields.length; i++) {
        const r = results[i];
        if (r.status === 'fulfilled') {
          const clean = (r.value.values || []).filter((v) => v != null && v !== '');
          opts[fields[i]] = sortValues(fields[i], clean);
        }
      }
    } finally {
      if (seq === distinctSeq) distinctLoading = false;
    }
  }
  function scheduleDistinct() {
    if (distinctTimer) clearTimeout(distinctTimer);
    distinctTimer = setTimeout(loadDistinct, 250);
  }

  // Filters apply themselves; there is no Apply button. Picking from a dropdown
  // or a date is a finished decision, so it runs almost at once. Typing is not:
  // each keystroke pushes the run back, and the search fires once you stop for
  // TYPING_DELAY -- otherwise "6aa0907d..." would fire a query per character.
  const PICK_DELAY = 250;
  const TYPING_DELAY = 2000;
  let applyTimer = null;
  let applyDelay = Infinity;
  function scheduleApply(delay) {
    // A slow request must never push back an apply that is already about to
    // run; the typed value is in `filter` either way and rides along with it.
    if (applyTimer !== null && delay > applyDelay) return;
    if (applyTimer !== null) clearTimeout(applyTimer);
    applyDelay = delay;
    applyTimer = setTimeout(() => {
      applyTimer = null;
      applyDelay = Infinity;
      load(true);
    }, delay);
  }

  // Customer / people pickers. Backed by enswitch.* via /directory/*.
  // Bound as single-element arrays for the MultiSelect single-mode trigger,
  // then projected back into filter.customer_id / filter.people_id strings.
  let customers = $state([]);          // [{ id, name }]
  let people = $state([]);             // [{ id, first_name, last_name, email, customer_id }]
  let phones = $state([]);             // [{ phone_id, extension, description, customer_id }]
  let directoryDown = $state(false);   // true when the directory can't be reached
  let directoryReason = $state('');    // the actual failure message, shown in the hint
  const idSeed = (v) => String(v || '').split(',').map((x) => Number(x.trim())).filter((n) => Number.isFinite(n) && n !== 0);
  const strSeed = (v) => String(v || '').split(',').map((x) => x.trim()).filter(Boolean);
  let customerSel = $state(idSeed(filter.customer_id));
  let peopleSel   = $state(idSeed(filter.people_id));
  let phoneSel    = $state(strSeed(filter.phone_id));
  // What was typed into the phone picker. Its list is the one directory list
  // too big to ship whole (~45k SIP lines), so the search runs on the server.
  let phoneQuery  = $state('');

  // How many filters are narrowing the result set right now (from/to are
  // always set, so they don't count). Shown as a chip on the collapsed header
  // so you can tell at a glance whether a filter is hidden behind the collapse.
  const activeFilters = $derived(
    [filter.levels, filter.applications, filter.hostnames, filter.sections,
     filter.versions, filter.log_types]
      .reduce((n, a) => n + (a.length ? 1 : 0), 0)
    + [filter.customer_id, filter.people_id, filter.phone_id, filter.call_id,
       filter.transaction_id, filter.path, filter.log_id]
      .reduce((n, s) => n + (s ? 1 : 0), 0)
  );

  // Levels render as the same coloured pill they get in the list, so the value
  // you tick looks like the rows it will bring back.
  const levelOptions = $derived(
    (opts.level || []).map((v) => ({ value: v, label: v, chipClass: levelClass(v) }))
  );
  const customerOptions = $derived(
    customers.map((c) => ({ value: c.id, label: c.name ? `${c.name} (#${c.id})` : `#${c.id}` }))
  );
  const peopleOptions = $derived(
    people.map((p) => {
      const name = [p.last_name, p.first_name].filter(Boolean).join(', ');
      const tail = [p.username, p.email, '#' + p.id].filter(Boolean).join(' · ');
      return { value: p.id, label: `${name || '(unnamed)'} — ${tail}` };
    })
  );
  // Value is the 7-digit SIP line, which is what a log's meta.phoneId holds.
  // The label adds the extension and whoever the line belongs to.
  const phoneOptions = $derived(
    phones.map((p) => {
      const tail = [p.extension && `ext ${p.extension}`, p.description].filter(Boolean).join(' · ');
      return { value: p.phone_id, label: tail ? `${p.phone_id} — ${tail}` : p.phone_id };
    })
  );

  async function loadCustomers() {
    directoryCtl?.abort();
    directoryCtl = new AbortController();
    try {
      const r = await api.listCustomers({ signal: directoryCtl.signal });
      customers = r.customers || [];
      directoryDown = false;
      directoryReason = '';
    } catch (e) {
      if (isAbort(e)) return;
      // Any non-abort failure means the picker can't be populated -- a 503
      // ("directory not available") when MariaDB isn't configured, a 500 SQL
      // error, or a gateway timeout. Mark it down and keep the actual reason
      // so the hint explains why the dropdown is empty instead of silently
      // showing "Any" with no options.
      directoryDown = true;
      directoryReason = e.message || 'directory request failed';
      customers = [];
    }
  }
  let peopleSeq = 0;
  let peopleCtl = null;
  async function loadPeople(customerIDs) {
    peopleCtl?.abort();
    // Users are listed per customer. Asking for every user in the directory is
    // ~52k rows / ~5MB of JSON on each visit, so wait for a customer instead of
    // paying that on load.
    const ids = (customerIDs || []).filter(Boolean);
    if (!ids.length) {
      people = [];
      return;
    }
    peopleCtl = new AbortController();
    const seq = ++peopleSeq;
    // Drop the previous customer's users right away. They are about to be
    // replaced, and leaving them in place lets the prune effect below validate
    // a freshly clicked user against the OLD list and throw it away.
    people = [];
    try {
      // One request per selected customer, merged. The endpoint is per-customer
      // and the selection is a handful at most, so this stays far cheaper than
      // pulling the whole directory.
      const pages = await Promise.all(
        ids.map((id) => api.listPeople(id, { signal: peopleCtl.signal }))
      );
      if (seq !== peopleSeq) return;
      const seen = new Set();
      const merged = [];
      for (const page of pages) {
        for (const person of page.people || []) {
          if (seen.has(person.id)) continue;
          seen.add(person.id);
          merged.push(person);
        }
      }
      people = merged;
    } catch (e) {
      if (isAbort(e)) return;
      if (seq !== peopleSeq) return;
      directoryDown = true;
      directoryReason = e.message || 'directory request failed';
      people = [];
    }
  }

  // The phone list is the one directory list that can't be pulled whole:
  // enswitch.phones is ~45k SIP lines. With customers picked it lists theirs;
  // with none, a typed query searches every customer server-side, which is
  // what makes "find this one line" possible without choosing a company
  // first. Under 3 characters and no customer there is nothing worth asking
  // for -- every prefix matches.
  const PHONE_SEARCH_MIN = 3;
  let phonesSeq = 0;
  let phonesCtl = null;
  async function loadPhones(customerIDs, query) {
    phonesCtl?.abort();
    const ids = (customerIDs || []).filter(Boolean);
    const q = (query || '').trim();
    if (!ids.length && q.length < PHONE_SEARCH_MIN) {
      phones = [];
      return;
    }
    phonesCtl = new AbortController();
    const seq = ++phonesSeq;
    try {
      const pages = ids.length
        ? await Promise.all(ids.map((id) => api.listPhones({ customerId: id, q }, { signal: phonesCtl.signal })))
        : [await api.listPhones({ q }, { signal: phonesCtl.signal })];
      if (seq !== phonesSeq) return;
      const seen = new Set();
      const merged = [];
      for (const page of pages) {
        for (const line of page.phones || []) {
          if (seen.has(line.phone_id)) continue;
          seen.add(line.phone_id);
          merged.push(line);
        }
      }
      phones = merged;
    } catch (e) {
      if (isAbort(e)) return;
      if (seq !== phonesSeq) return;
      phones = [];
    }
  }

  // Typing in the phone picker hits the database, so it waits for a pause --
  // shorter than the filter's own typing delay, because this only repopulates
  // a dropdown rather than re-running the search.
  const PHONE_SEARCH_DELAY = 400;
  let phoneQueryTimer = null;
  function onPhoneSearch(q) {
    if (phoneQueryTimer !== null) clearTimeout(phoneQueryTimer);
    phoneQueryTimer = setTimeout(() => {
      phoneQueryTimer = null;
      phoneQuery = q;
    }, PHONE_SEARCH_DELAY);
  }

  // Mirror the pickers into the single-string filter fields the /logs query
  // uses. This MUST clear as well as set: unticking a customer empties the
  // selection, and if that didn't write back an empty string the old id stayed
  // in the filter and the search kept being narrowed by a customer the UI no
  // longer showed as chosen.
  //
  // Skipped while the directory is down, because then the id boxes are bound to
  // these same fields directly and the (always empty) pickers would wipe
  // whatever was typed.
  $effect(() => {
    if (directoryDown) return;
    filter.customer_id = customerSel.join(',');
  });
  $effect(() => {
    if (directoryDown) return;
    filter.people_id = peopleSel.join(',');
  });
  $effect(() => {
    if (directoryDown) return;
    filter.phone_id = phoneSel.join(',');
  });

  // Anything chosen from a control applies right away.
  $effect(() => {
    filter.from; filter.to;
    filter.levels.length; filter.applications.length; filter.hostnames.length;
    filter.sections.length; filter.versions.length; filter.log_types.length;
    customerSel.length; peopleSel.length; phoneSel.length;
    if (!started) return;
    scheduleApply(PICK_DELAY);
  });

  // Anything typed waits for a pause.
  $effect(() => {
    filter.phone_id; filter.call_id; filter.transaction_id; filter.path; filter.log_id;
    filter.customer_id; filter.people_id;
    if (!started) return;
    scheduleApply(TYPING_DELAY);
  });

  // Reload the people list whenever the customer selection changes. With no
  // customer there is nothing to list -- see loadPeople.
  $effect(() => {
    loadPeople([...customerSel]);
  });
  $effect(() => {
    loadPhones([...customerSel], phoneQuery);
  });
  $effect(() => {
    if (!peopleSel.length || !people.length) return;
    const owned = new Set(people.map((p) => p.id));
    const kept = peopleSel.filter((id) => owned.has(id));
    if (kept.length !== peopleSel.length) peopleSel = kept;
  });

  async function load(resetPage = true) {
    logsCtl?.abort();
    logsCtl = new AbortController();
    loading = true;
    err = '';
    try {
      if (resetPage) {
        cursor = '';
        // Selections track log _ids that may no longer match the new filter.
        // Clearing keeps the toolbar count honest.
        if (selected.size) selected = new Set();
        lastSelIndex = -1;
      }
      const r = await api.listLogs({ ...filter, before_id: cursor, limit }, { signal: logsCtl.signal });
      const rows = r.logs || [];
      logs = resetPage ? rows : logs.concat(rows);
      // A short page means there is nothing older left. It is the only
      // end-of-data signal available: the server deliberately does not count
      // (too slow), so its `total` is a lower bound, never a finish line.
      atEnd = rows.length < limit;
      if (rows.length) cursor = rows[rows.length - 1]._id;
      // We know exactly how many we hold; once the end is reached that IS the
      // total, so the count stops reading "40+" and settles on the real number.
      total = logs.length;
      totalExact = atEnd;
      // Mirror filter state into the URL so the page is shareable.
      const u = new URLSearchParams();
      for (const [k, v] of Object.entries(filter)) {
        if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) continue;
        u.set(k, Array.isArray(v) ? v.join(',') : String(v));
      }
      const qs = u.toString();
      history.replaceState(null, '', '/logs' + (qs ? '?' + qs : ''));
    } catch (e) {
      if (isAbort(e)) return; // request superseded or reset; not a user-visible error
      err = e.message;
      ui.snack(e.message, 'error');
    } finally {
      loading = false;
    }
  }

  async function loadMore() {
    if (loading || atEnd) return;
    await load(false);
    // On a tall viewport the sentinel can stay on-screen after load, so
    // IntersectionObserver wouldn't re-fire (no transition). Manually keep
    // pulling pages until the sentinel scrolls off or there's nothing more.
    if (sentinel && !atEnd) {
      const r = sentinel.getBoundingClientRect();
      if (r.top < window.innerHeight) queueMicrotask(loadMore);
    }
  }

  function reset() {
    // Cancel any in-flight requests; both abort signals propagate through to
    // the Go handler's r.Context() which the mongo/mariadb drivers respect,
    // so the underlying database query is cancelled too.
    logsCtl?.abort();
    distinctCtl?.abort();
    peopleCtl?.abort();
    phonesCtl?.abort();
    if (distinctTimer) { clearTimeout(distinctTimer); distinctTimer = null; }
    if (phoneQueryTimer !== null) { clearTimeout(phoneQueryTimer); phoneQueryTimer = null; }
    if (applyTimer !== null) { clearTimeout(applyTimer); applyTimer = null; applyDelay = Infinity; }

    // Clear EVERY piece of state the user can see -- the multi-value filter
    // arrays live on `filter`, the customer/user pickers live on separate
    // single-element arrays, and the URL needs to drop its query string.
    filter = {
      from: defaultFrom(), to: defaultTo(),
      levels: [], hostnames: [], applications: [], sections: [], versions: [], log_types: [],
      customer_id: '', people_id: '', phone_id: '', call_id: '', transaction_id: '', path: '', log_id: '',
    };
    customerSel = [];
    peopleSel = [];
    phoneSel = [];
    phoneQuery = '';
    history.replaceState(null, '', '/logs');
    load();
  }

  function toggle(id) {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    expanded = next;
  }

  // --- Selection + per-log copy ----------------------------------------
  // Click toggles one; shift-click selects the range from the last-clicked row
  // to this one (adds them). onclick (not onchange) so we get shiftKey.
  function toggleSelect(index, shiftKey) {
    const next = new Set(selected);
    if (shiftKey && lastSelIndex >= 0) {
      const lo = Math.min(lastSelIndex, index), hi = Math.max(lastSelIndex, index);
      for (let i = lo; i <= hi; i++) next.add(logs[i]._id);
    } else {
      const id = logs[index]._id;
      if (next.has(id)) next.delete(id); else next.add(id);
    }
    selected = next;
    lastSelIndex = index;
  }
  function selectAllOnPage() {
    const allOn = logs.length > 0 && logs.every((d) => selected.has(d._id));
    selected = allOn ? new Set() : new Set(logs.map((d) => d._id));
  }
  function clearSelection() { selected = new Set(); lastSelIndex = -1; }
  let allOnPageSelected = $derived(logs.length > 0 && logs.every((d) => selected.has(d._id)));

  async function copyDoc(doc) {
    try {
      await navigator.clipboard.writeText(JSON.stringify(doc, null, 2));
      ui.snack('Log copied', 'success', 1500);
    } catch (e) { ui.snack('Copy failed: ' + e.message, 'error'); }
  }
  async function copySelected() {
    const docs = logs.filter((d) => selected.has(d._id));
    if (!docs.length) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(docs, null, 2));
      ui.snack(`Copied ${docs.length.toLocaleString()} log${docs.length === 1 ? '' : 's'}`, 'success', 1500);
    } catch (e) { ui.snack('Copy failed: ' + e.message, 'error'); }
  }
  function downloadSelected() {
    const docs = logs.filter((d) => selected.has(d._id));
    if (!docs.length) return;
    const blob = new Blob([JSON.stringify(docs, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    a.href = url;
    a.download = `logs-selected-${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    ui.snack(`Downloaded ${docs.length.toLocaleString()} log${docs.length === 1 ? '' : 's'}`, 'success');
  }

  // The log list scrolls inside its own bounded container (the filter card
  // above stays put). Bound so the infinite-scroll observer can use it as
  // the intersection root.
  let listScroll = $state(null);

  // --- Infinite scroll --------------------------------------------------
  // Watch the sentinel; when it intersects, kick off the next page. The
  // observer root is the bounded list container so intersection is measured
  // against that scroll area, not the viewport.
  $effect(() => {
    if (!sentinel) return;
    const obs = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      if (loading || atEnd) return;
      loadMore();
    }, { root: listScroll || null, rootMargin: '200px 0px' });
    obs.observe(sentinel);
    return () => obs.disconnect();
  });

  // Download the full filtered set (not just the page on screen) as JSON.
  // Pages 200 at a time so the server isn't asked to materialise everything
  // in one request, but caps at 50k rows to keep the browser sane.
  let downloading = $state(false);
  async function download() {
    if (downloading) return;
    downloading = true;
    try {
      const all = [];
      const PAGE = 200, MAX = 50_000;
      let before = '';
      while (all.length < MAX) {
        const r = await api.listLogs({ ...filter, before_id: before, limit: PAGE });
        const rows = r.logs || [];
        all.push(...rows);
        if (rows.length < PAGE) break; // last page
        before = rows[rows.length - 1]._id;
      }
      const blob = new Blob([JSON.stringify(all, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.href = url;
      a.download = `logs-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      ui.snack(`Downloaded ${all.length.toLocaleString()} log entries`, 'success');
    } catch (e) {
      ui.snack('Download failed: ' + e.message, 'error');
    } finally {
      downloading = false;
    }
  }

  // Pretty time + identity helpers.
  function fmtTs(doc) {
    const t = doc.timestamp ? new Date(doc.timestamp) : oidToDate(doc._id);
    return t ? t.toLocaleString() : '—';
  }
  function oidToDate(oid) {
    if (!oid || typeof oid !== 'string' || oid.length < 8) return null;
    return new Date(parseInt(oid.slice(0, 8), 16) * 1000);
  }
  function summary(doc) {
    const m = doc.message;
    if (m == null) return '';
    if (typeof m === 'string') return m;
    try { return JSON.stringify(m); } catch { return String(m); }
  }
  // A log can carry several transaction ids, so the clickable form is a list --
  // the joined string above is only good for a tooltip.
  function txnList(doc) {
    const t = doc.meta?.transactionId;
    if (t == null) return [];
    return (Array.isArray(t) ? t : [t]).map((v) => String(v)).filter(Boolean);
  }
  // The identifiers on a log that are worth pivoting on. Each becomes a chip
  // under the expanded entry so you can jump from one log to every log sharing
  // that id without copying it into the filter by hand.
  function identifiers(doc) {
    const m = doc.meta || {};
    const out = txnList(doc).map((v) => ({ label: 'transaction', field: 'transaction_id', value: v, customerId: '' }));
    // A user is only listed under its customer, so a user chip carries the
    // customer from the same log -- selecting the user alone would be validated
    // against the wrong customer's list and silently dropped.
    const ownCustomer = m.customerId != null && Number(m.customerId) !== 0 ? String(m.customerId) : '';
    const scalar = [
      ['customer', 'customer_id', m.customerId, ''],
      ['user', 'people_id', m.peopleId, ownCustomer],
      ['call', 'call_id', m.callId, ''],
      ['phone', 'phone_id', m.phoneId, ''],
    ];
    for (const [label, field, raw, customerId] of scalar) {
      if (raw == null || raw === '') continue;
      // A zero customer/user id means "none" in the logs, and the server drops
      // a zero from the query -- a chip for it would look clickable and change
      // nothing.
      if ((field === 'customer_id' || field === 'people_id') && Number(raw) === 0) continue;
      out.push({ label, field, value: String(raw), customerId });
    }
    return out;
  }
  // Narrow the current search to one identifier. These filters hold a single
  // value, so clicking a second one REPLACES the first rather than widening the
  // search -- same as typing over the text box.
  function filterBy(field, value, customerId = '') {
    const v = String(value ?? '').trim();
    if (!v) return;
    // Add to the selection rather than replace it: the customer, user and phone
    // pickers are multi-select, so clicking a second one widens the search the
    // same way ticking it in the dropdown does. Everything else holds a single
    // value and is replaced.
    const add = (list, n) => (list.includes(n) ? list : [...list, n]);
    if (field === 'customer_id') customerSel = add(customerSel, Number(v));
    if (field === 'people_id') {
      if (customerId) customerSel = add(customerSel, Number(customerId));
      peopleSel = add(peopleSel, Number(v));
    }
    if (field === 'phone_id') phoneSel = add(phoneSel, v);
    filter[field] = field === 'customer_id' ? customerSel.join(',')
      : field === 'people_id' ? peopleSel.join(',')
      : field === 'phone_id' ? phoneSel.join(',')
      : v;
    load(true);
  }
  function logType(doc) { return doc.meta?.logType || ''; }
  function pretty(doc) {
    try { return JSON.stringify(doc, null, 2); } catch { return String(doc); }
  }
  // highlight.js escapes the source before colouring it, so match its output
  // rather than the raw value.
  function escHtml(v) {
    return String(v)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#x27;');
  }

  // Wrap every identifier value in the coloured JSON with a button, so the ids
  // are clickable where they are actually read -- inside the expanded entry --
  // not only on the chip row. Matching is a plain string search against hljs's
  // own escaped output, and the match is re-inserted verbatim, so nothing from
  // the database is ever re-introduced as live HTML.
  function linkIds(html, doc) {
    for (const { field, value, label, customerId } of identifiers(doc)) {
      const v = escHtml(value);
      const attrs = `data-idfield="${escHtml(field)}" data-idvalue="${v}"`
        + ` data-idcustomer="${escHtml(customerId || '')}"`
        + ` title="Show every log with this ${escHtml(label)} id"`;
      for (const needle of [
        `<span class="hljs-string">&quot;${v}&quot;</span>`,
        `<span class="hljs-number">${v}</span>`,
      ]) {
        if (!html.includes(needle)) continue;
        html = html.split(needle).join(`<button type="button" class="logs-jsonid" ${attrs}>${needle}</button>`);
      }
    }
    return html;
  }
  function highlightJson(doc) {
    return linkIds(hljs.highlight(pretty(doc), { language: 'json' }).value, doc);
  }
  // {@html} content carries no Svelte handlers, so the id buttons inside the
  // rendered JSON are caught by delegation. Attached as an action rather than
  // an inline handler: the <pre> itself is not interactive (the buttons within
  // it are), and an inline listener on it would be an accessibility smell.
  // Double-click anywhere on a row expands or collapses it. Deliberately NOT
  // single click -- that stays free for selecting and copying text out of a
  // row. A double-click that lands on a control (the checkbox, the chevron) is
  // ignored so the row doesn't toggle on top of what the control already did.
  function dblToggle(node, id) {
    let current = id;
    const onDoubleClick = (e) => {
      if (e.target.closest?.('button, input, a, label')) return;
      toggle(current);
    };
    node.addEventListener('dblclick', onDoubleClick);
    return {
      update(next) { current = next; },
      destroy() { node.removeEventListener('dblclick', onDoubleClick); },
    };
  }

  function idClicks(node) {
    const onClick = (e) => {
      const el = e.target.closest?.('[data-idfield]');
      if (!el) return;
      e.stopPropagation();
      filterBy(el.dataset.idfield, el.dataset.idvalue, el.dataset.idcustomer || '');
    };
    node.addEventListener('click', onClick);
    return { destroy: () => node.removeEventListener('click', onClick) };
  }

  const LEVEL_COLORS = {
    error:  'bg-error/15 text-error',
    fatal:  'bg-error/15 text-error',
    warn:   'bg-amber-500/15 text-amber-700 dark:text-amber-300',
    info:   'bg-primary/15 text-primary',
    debug:  'bg-surface-variant text-on-surface-variant',
    verbose:'bg-surface-variant text-on-surface-variant',
  };
  function levelClass(l) { return LEVEL_COLORS[(l || '').toLowerCase()] || 'bg-surface-variant text-on-surface-variant'; }

  // Re-fetch dropdown values whenever the date range or any narrowing
  // multi-select changes. The aggregation on the server is $match +
  // $sort + $limit(5000) + $group so even sippy-proxy narrowing stays
  // sub-second. Fields that don't gate the dropdowns (path, customer_id,
  // ...) are intentionally left out of the dependency list.
  $effect(() => {
    filter.from; filter.to;
    filter.levels; filter.hostnames; filter.applications;
    filter.sections; filter.versions; filter.log_types;
    scheduleDistinct();
  });

  onMount(() => {
    loadCustomers();
    load();
    started = true;
  });
</script>

<div class="logs-page">
<!-- One flat filter card, one kind of control per row: when and what (From,
     To, Levels, Applications), then the log's own facets, then the enswitch
     directory pickers, then the ids you type. Mixing a dropdown into a row of
     text boxes reads as a mistake -- their labels sit in different places and
     only one has a chevron. Each row is its own auto-fit grid, so rows wrap
     independently on a narrow viewport. Reset sits in the header row. -->
<section class="logs-filter-card bg-surface-container rounded-2xl mb-4" class:logs-filter-collapsed={!filtersOpen}>
  {#if loading || downloading}
    <div class="logs-progress" role="progressbar" aria-label="Loading">
      <div class="logs-progress-bar"></div>
    </div>
  {/if}
  <!-- Collapse header: hides the inputs so the table can take the whole page.
       The active-filter count stays visible while collapsed, and Reset lives up
       here rather than under the inputs -- there is no Apply button left for it
       to sit beside, and it has to stay reachable while the card is collapsed. -->
  <div class="logs-filter-head">
    <button type="button" class="logs-filter-toggle" onclick={() => (filtersOpen = !filtersOpen)} aria-expanded={filtersOpen}>
      <span class="material-symbols-outlined logs-filter-chevron" style:transform={filtersOpen ? 'rotate(90deg)' : ''}>chevron_right</span>
      <span class="font-medium">Filters</span>
      {#if activeFilters > 0}<span class="logs-filter-count">{activeFilters}</span>{/if}
    </button>
    {#if filtersOpen}
      <span class="logs-filter-hint">Filters apply as you change them</span>
    {/if}
    <button
      type="button"
      onclick={reset}
      class="px-3 py-1.5 rounded-full text-sm bg-surface-container-high hover:bg-surface-container-highest text-on-surface shrink-0"
      title="Clear every filter and go back to the default window"
    >
      <span class="material-symbols-outlined text-base align-middle">filter_alt_off</span>
      Reset
    </button>
  </div>
  {#if filtersOpen}
  <!-- Filters apply themselves as you change them. The <form> stays so Enter
       inside any field searches at once instead of waiting out the typing
       pause, and so Tab still walks the natural input order. -->
  <form class="logs-filter-form" onsubmit={(e) => { e.preventDefault(); scheduleApply(0); }}>
    <!-- Each row is its own auto-fit grid so the row's children always
         stretch to fill the available width. The 4-item rows show 4
         across; the 6-item row shows 6 across; on a narrower viewport
         each row wraps independently. -->
    <div class="logs-filter-row items-end">
      <DateField   label="From" bind:value={filter.from} />
      <DateField   label="To"   bind:value={filter.to} />
      <MultiSelect label="Levels" bind:values={filter.levels} options={levelOptions} placeholder="Any" />
      <MultiSelect label="Applications" bind:values={filter.applications} options={opts.applicationName} placeholder="Any" />
    </div>

    <div class="logs-filter-row items-end mt-3">
      <MultiSelect label="Hostnames"  bind:values={filter.hostnames} options={opts.hostname}        placeholder="Any" />
      <MultiSelect label="Sections"   bind:values={filter.sections}  options={opts.section}         placeholder="Any" />
      <MultiSelect label="Versions"   bind:values={filter.versions}  options={opts.version}         placeholder="Any" />
      <MultiSelect label="Log types"  bind:values={filter.log_types} options={opts['meta.logType']} placeholder="Any" />
    </div>

    <!-- The directory row. These three chain -- a customer narrows its users
         and its SIP lines -- and they are the only pickers backed by enswitch
         rather than by the logs, so they sit together rather than trailing the
         log facets above or leading the typed ids below. -->
    <div class="logs-filter-row items-end mt-3">
      {#if directoryDown}
        <TextField label="Customer ID" bind:value={filter.customer_id} inputmode="numeric" />
        <TextField label="User ID"     bind:value={filter.people_id}   inputmode="numeric" />
        <TextField label="Phone ID"    bind:value={filter.phone_id}    inputmode="numeric" />
      {:else}
        <MultiSelect label="Customers" bind:values={customerSel} options={customerOptions} placeholder="Any" />
        <MultiSelect label="Users"     bind:values={peopleSel}   options={peopleOptions}   placeholder={customerSel.length ? 'Any user in these customers' : 'Pick a customer first'} />
        <MultiSelect
          label="Phone ID"
          bind:values={phoneSel}
          options={phoneOptions}
          onsearch={onPhoneSearch}
          placeholder={customerSel.length ? 'Any line on these customers' : 'Type a line or extension'} />
      {/if}
    </div>

    <div class="logs-filter-row items-end mt-3">
      <TextField label="Call ID"        bind:value={filter.call_id} />
      <TextField label="Transaction ID" bind:value={filter.transaction_id} />
      <TextField label="Path"           bind:value={filter.path} />
      <TextField label="Log ID"         bind:value={filter.log_id} />
    </div>

    {#if directoryDown}
      <p class="text-xs text-on-surface-variant mt-3">
        <span class="material-symbols-outlined text-base align-middle">info</span>
        Customer / user pickers need MariaDB access and it couldn't be reached, so they're empty. You can still type a numeric Customer ID / User ID below, and every other filter works.
        {#if directoryReason}<span class="block mt-1 opacity-80">Reason: {directoryReason}</span>{/if}
      </p>
    {/if}

  </form>
  {/if}
</section>

<!-- List toolbar: match count + actions, directly above the list. -->
<div class="flex items-center justify-between mb-3 gap-3 flex-wrap shrink-0">
  <span class="text-sm text-on-surface-variant flex items-center gap-2">
    {#if loading || downloading}
      <span class="material-symbols-outlined text-primary logs-spin" style="font-size: 18px;" aria-hidden="true" title="Loading">progress_activity</span>
    {/if}
    {total.toLocaleString()}{totalExact ? '' : '+'} matching
  </span>
  <div class="flex items-center gap-2">
    <button
      onclick={() => load()}
      disabled={loading}
      class="px-4 py-2 rounded-full text-sm bg-surface-container hover:bg-surface-container-high text-on-surface disabled:opacity-50"
      title="Reload the current filter"
    >
      <span class="material-symbols-outlined text-base align-middle">refresh</span>
      Refresh
    </button>
    <button
      onclick={download}
      disabled={downloading || logs.length === 0}
      class="px-4 py-2 rounded-full text-sm bg-surface-container hover:bg-surface-container-high text-on-surface disabled:opacity-50"
      title="Download all matching log entries as JSON"
    >
      <span class="material-symbols-outlined text-base align-middle">download</span>
      {downloading ? 'Downloading…' : 'Download'}
    </button>
  </div>
</div>



{#if loading && logs.length === 0}
  <p class="text-on-surface-variant">Loading...</p>
{:else if err}
  <p class="text-error">{err}</p>
{:else if logs.length === 0}
  <div class="bg-surface-container rounded-2xl p-12 text-center text-on-surface-variant">
    <span class="material-symbols-outlined text-5xl mb-2">manage_search</span>
    <p>No log entries match the current filter.</p>
  </div>
{:else}
  <div class="logs-list-scroll" bind:this={listScroll}>
  <div class="logs-table">
    <!-- Header row. Subgrid keeps these cells aligned with every data row. -->
    <div class="logs-thead">
      <span class="logs-th logs-th-check">
        <label class="logs-checkbox-wrap" title="Select all on this page">
          <input type="checkbox" checked={allOnPageSelected} onchange={selectAllOnPage} class="logs-checkbox" aria-label="Select all on page" />
        </label>
      </span>
      <span class="logs-th" aria-hidden="true"></span>
      <span class="logs-th">Id</span>
      <span class="logs-th">Timestamp</span>
      <span class="logs-th">Level</span>
      <span class="logs-th">Type</span>
      <span class="logs-th">Message</span>
      <span class="logs-th">Application</span>
      <span class="logs-th">Hostname</span>
    </div>

    {#each logs as doc, i (doc._id)}
      {@const open = expanded.has(doc._id)}
      {@const sel = selected.has(doc._id)}
      <div class="logs-entry">
      <div class="logs-trow" class:logs-row-selected={sel} class:logs-row-open={open} use:dblToggle={doc._id}>
        <label class="logs-td logs-td-check">
          <input type="checkbox" checked={sel} onclick={(e) => toggleSelect(i, e.shiftKey)} class="logs-checkbox" aria-label="Select log" />
        </label>
        <button type="button" class="logs-td logs-td-chevron" onclick={() => toggle(doc._id)} aria-expanded={open} aria-label={open ? 'Collapse entry' : 'Expand entry'}>
          <span class="material-symbols-outlined logs-chevron" style:transform={open ? 'rotate(90deg)' : ''}>chevron_right</span>
        </button>
        <div class="logs-td logs-cell-mono" title={doc._id}>{doc._id}</div>
        <div class="logs-td logs-cell-nowrap">{fmtTs(doc)}</div>
        <div class="logs-td"><span class="px-2 py-0.5 rounded-md text-xs font-medium {levelClass(doc.level)}">{doc.level || '—'}</span></div>
        <div class="logs-td logs-cell-mono" title={logType(doc)}>{logType(doc) || '—'}</div>
        <div class="logs-td logs-cell-msg" title={summary(doc)}>{summary(doc)}</div>
        <div class="logs-td logs-cell-mono" title={doc.applicationName || ''}>{doc.applicationName || '—'}</div>
        <div class="logs-td logs-cell-mono" title={doc.hostname || ''}>{doc.hostname || '—'}</div>
      </div>
      {#if open}
        <div class="logs-json">
          <button type="button" class="logs-json-copy" onclick={() => copyDoc(doc)} title="Copy log JSON" aria-label="Copy log JSON">
            <span class="material-symbols-outlined text-base">content_copy</span>
          </button>
          {#if identifiers(doc).length}
            <div class="logs-idbar">
              <span class="logs-idbar-label">Filter by</span>
              {#each identifiers(doc) as id (id.field + id.value)}
                <button type="button" class="logs-idchip" onclick={() => filterBy(id.field, id.value, id.customerId)} title="Show every log with this {id.label} id">
                  <span class="logs-idchip-k">{id.label}</span>
                  <span class="logs-idchip-v">{id.value}</span>
                </button>
              {/each}
            </div>
          {/if}
          <pre class="hljs" use:idClicks><code class="language-json">{@html highlightJson(doc)}</code></pre>
        </div>
      {/if}
      </div>
    {/each}
  </div>

  <!-- Sentinel for IntersectionObserver-driven infinite scroll. Rendered
       only when there's more to load; the manual button stays as a
       fallback for keyboard / accessibility. Inside the scroll container so
       the observer (root = container) sees it. -->
  <div class="flex flex-col items-center mt-4 gap-2">
    {#if !atEnd}
      <div bind:this={sentinel} class="logs-sentinel" aria-hidden="true"></div>
      <button
        onclick={loadMore}
        disabled={loading}
        class="px-6 py-2 rounded-full text-sm bg-surface-container hover:bg-surface-container-high text-on-surface disabled:opacity-50"
      >
        {loading ? 'Loading...' : `Load ${limit} more`}
      </button>
    {:else}
      <span class="text-xs text-on-surface-variant">All {logs.length.toLocaleString()} matching entries shown.</span>
    {/if}
  </div>
  </div>

  <!-- Floating selection toolbar. Pinned to the bottom-center, only visible
       when something is selected. Action buttons act on the in-memory rows
       (matching what's actually loaded), not the full filter set. -->
  {#if selected.size > 0}
    <div class="logs-selbar" role="toolbar" aria-label="Selection actions">
      <span class="logs-selbar-count">{selected.size.toLocaleString()} selected</span>
      <button onclick={copySelected} class="logs-selbar-btn">
        <span class="material-symbols-outlined text-base">content_copy</span>
        Copy
      </button>
      <button onclick={downloadSelected} class="logs-selbar-btn">
        <span class="material-symbols-outlined text-base">download</span>
        Download
      </button>
      <button onclick={clearSelection} class="logs-selbar-btn-text" title="Clear selection">
        <span class="material-symbols-outlined text-base">close</span>
      </button>
    </div>
  {/if}
{/if}
</div>

<style>
  /* Each filter row is its own auto-fit grid: children share the row's
     full width, stretching to fill leftover space (so a 4-item row
     doesn't leave gaps to its right). On narrower viewports the row
     wraps independently. Min track width keeps fields readable. */
  .logs-filter-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 0.75rem;
  }
  .logs-filter-fullrow { grid-column: 1 / -1; }

  /* Fill the content pane and lay out as a column: filter card + toolbar take
     their natural height, the list flexes to fill the rest. This makes the
     list the ONLY scroller (the pane itself never overflows), so there's no
     page-scroller + list-scroller double scrollbar. */
  .logs-page {
    block-size: 100%;
    display: flex;
    flex-direction: column;
    min-block-size: 0;
  }

  /* Collapse header + body padding. The card itself has no padding now (so the
     header bar can sit flush); the toggle and the form supply their own. */
  .logs-filter-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding-inline-end: 1rem;
  }
  .logs-filter-hint {
    margin-inline-start: auto;
    font-size: 0.75rem;
    white-space: nowrap;
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
  /* No room for the hint next to the field rows on a narrow viewport; the
     behaviour it describes is obvious once a filter has been changed once. */
  @media (max-width: 720px) {
    .logs-filter-hint { display: none; }
  }
  .logs-filter-toggle {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
    min-inline-size: 0;
    padding: 0.75rem 1rem;
    background: transparent;
    border: 0;
    color: inherit;
    font-size: 0.875rem;
    cursor: pointer;
  }
  .logs-filter-collapsed .logs-filter-toggle { padding-block: 0.625rem; }
  .logs-filter-chevron {
    font-size: 20px;
    color: rgb(var(--md-sys-color-on-surface-variant));
    transition: transform 150ms;
  }
  .logs-filter-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-inline-size: 20px;
    block-size: 20px;
    padding-inline: 6px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    background: rgb(var(--md-sys-color-primary));
    color: rgb(var(--md-sys-color-on-primary));
  }
  .logs-filter-form { padding: 0 1rem 1rem; }

  /* M3 indeterminate linear progress indicator -- a 3px sliver flush along
     the top edge of the filter card, exactly where M3 places progress on
     a contained surface. Sits inside the card's clipping bounds so the
     border-radius caps it. The card is `position: relative` to anchor it. */
  .logs-filter-card { position: relative; overflow: hidden; flex-shrink: 0; }
  .logs-progress {
    position: absolute;
    inset-block-start: 0;
    inset-inline: 0;
    block-size: 3px;
    background: rgb(var(--md-sys-color-primary) / 0.16);
    overflow: hidden;
  }
  .logs-progress-bar {
    block-size: 100%;
    inline-size: 33%;
    background: rgb(var(--md-sys-color-primary));
    animation: logs-progress-slide 1.4s cubic-bezier(.4,0,.2,1) infinite;
  }
  @keyframes logs-progress-slide {
    0%   { transform: translateX(-110%); }
    50%  { transform: translateX(120%); }
    100% { transform: translateX(330%); }
  }

  .logs-spin { animation: logs-spin 1s linear infinite; }
  @keyframes logs-spin { to { transform: rotate(360deg); } }

  /* The log list is the single scroller: it flexes to fill whatever height is
     left under the (collapsible) filter card + toolbar, so the page itself
     never gets a second scrollbar. overflow on BOTH axes -- the wide table
     scrolls horizontally on narrow viewports instead of crushing columns. The
     rounded surface + clipping live here so the sticky header below works (a
     scroll container on the table itself would trap the sticky). */
  .logs-list-scroll {
    flex: 1;
    min-block-size: 0;
    overflow: auto;
    background: rgb(var(--md-sys-color-surface-container));
    border-radius: 16px;
  }

  /* Grid "table": one track per field. The header and every row use
     `grid-template-columns: subgrid` so their cells align to these tracks --
     a real columnar layout, not nested flex. min-inline-size forces the
     scroll container to scroll-x rather than squish below readability. */
  .logs-table {
    display: grid;
    grid-template-columns:
      40px                     /* select */
      36px                     /* expand */
      minmax(150px, 1.1fr)     /* Id */
      minmax(140px, 1fr)       /* Timestamp */
      72px                     /* Level */
      minmax(150px, 1.2fr)     /* Type */
      minmax(240px, 2.4fr)     /* Message */
      minmax(120px, 1fr)       /* Application */
      minmax(130px, 1.1fr);    /* Hostname */
    align-content: start;
    min-inline-size: 900px;
    /* Header height, reused as the offset an expanded row sticks to. Pinned so
       the two can't drift apart and leave a gap or an overlap. */
    --logs-head-h: 37px;
  }

  /* One entry = its row plus, when open, its JSON. The wrapper exists so an
     expanded row's sticky positioning is bounded by its OWN entry; without it
     every open row would stay pinned under the header and stack up. Nested
     subgrid passes the table's columns through the extra level. */
  .logs-entry {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: subgrid;
  }
  .logs-thead,
  .logs-trow {
    display: grid;
    grid-template-columns: subgrid;
    grid-column: 1 / -1;
    align-items: center;
  }
  .logs-thead {
    position: sticky;
    inset-block-start: 0;
    z-index: 3;
    block-size: var(--logs-head-h);
    background: rgb(var(--md-sys-color-surface-container-high));
    border-block-end: 1px solid rgb(var(--md-sys-color-outline-variant));
  }
  .logs-th {
    padding: 0.625rem 0.75rem;
    font-size: 12px;
    font-weight: 600;
    color: rgb(var(--md-sys-color-on-surface-variant));
    white-space: nowrap;
  }
  .logs-trow { border-block-start: 1px solid rgb(var(--md-sys-color-outline-variant) / 0.5); }
  .logs-trow:hover { background: rgb(var(--md-sys-color-on-surface) / 0.04); }
  .logs-row-selected { background: rgb(var(--md-sys-color-primary) / 0.08); }

  /* An expanded row stays under the header while you read its JSON, so the
     chevron that closes it is always reachable on a long entry. The background
     must be opaque -- the JSON scrolls underneath and a translucent tint would
     let it show through. */
  .logs-trow.logs-row-open {
    position: sticky;
    inset-block-start: var(--logs-head-h);
    z-index: 2;
    background:
      linear-gradient(rgb(var(--md-sys-color-primary) / 0.10) 0 100%),
      rgb(var(--md-sys-color-surface-container));
    box-shadow: 0 1px 0 rgb(var(--md-sys-color-outline-variant));
  }
  .logs-trow.logs-row-open.logs-row-selected {
    background:
      linear-gradient(rgb(var(--md-sys-color-primary) / 0.18) 0 100%),
      rgb(var(--md-sys-color-surface-container));
  }
  .logs-td {
    min-inline-size: 0;
    padding: 0.5rem 0.75rem;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .logs-cell-mono { font-family: 'Menlo','Consolas',monospace; font-size: 12px; }
  .logs-td-check { display: flex; align-items: center; justify-content: center; padding-inline: 0; }
  .logs-td-chevron {
    display: flex; align-items: center; justify-content: center;
    padding: 0; border: 0; background: transparent; cursor: pointer;
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
  .logs-td-chevron:hover { color: rgb(var(--md-sys-color-primary)); }
  .logs-chevron { font-size: 20px; transition: transform 150ms; }

  /* Expanded JSON spans the full table width below its row. */
  .logs-json {
    grid-column: 1 / -1;
    position: relative;
    border-block-start: 1px solid rgb(var(--md-sys-color-outline-variant));
    background: rgb(var(--md-sys-color-surface));
  }
  .logs-json pre { margin: 0; padding: 0.75rem 1rem; overflow-x: auto; font-size: 12px; }

  /* Ids inside the expanded JSON are clickable in place: underlined on hover so
     they read as plain JSON until you reach for one. */
  .logs-jsonid {
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    color: inherit;
    cursor: pointer;
    border-radius: 3px;
  }
  .logs-jsonid:hover { background: rgb(var(--md-sys-color-primary) / 0.16); text-decoration: underline; }
  .logs-jsonid:focus-visible { outline: 2px solid rgb(var(--md-sys-color-primary)); outline-offset: 1px; }

  /* Pivot chips: click an id to narrow the search to every log that shares it. */
  .logs-idbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 0.625rem 1rem 0;
  }
  .logs-idbar-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgb(var(--md-sys-color-on-surface-variant));
    margin-inline-end: 2px;
  }
  .logs-idchip {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    max-inline-size: 100%;
    padding: 3px 10px;
    border: 1px solid rgb(var(--md-sys-color-outline-variant));
    border-radius: 999px;
    background: rgb(var(--md-sys-color-surface-container));
    color: rgb(var(--md-sys-color-on-surface));
    font: inherit;
    font-size: 12px;
    cursor: pointer;
  }
  .logs-idchip:hover {
    border-color: rgb(var(--md-sys-color-primary));
    background: rgb(var(--md-sys-color-primary) / 0.10);
  }
  .logs-idchip:focus-visible {
    outline: 2px solid rgb(var(--md-sys-color-primary));
    outline-offset: 2px;
  }
  .logs-idchip-k { color: rgb(var(--md-sys-color-on-surface-variant)); font-size: 11px; }
  .logs-idchip-v {
    font-family: 'Menlo','Consolas',monospace;
    font-size: 11px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* The transaction column stays a plain cell until you hover an id. */
  .logs-json-copy {
    position: absolute; inset-block-start: 6px; inset-inline-end: 10px;
    display: inline-flex; align-items: center; justify-content: center;
    inline-size: 30px; block-size: 30px; border-radius: 8px; z-index: 1;
    border: 0; cursor: pointer;
    background: rgb(var(--md-sys-color-surface-container));
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
  .logs-json-copy:hover { color: rgb(var(--md-sys-color-primary)); }

  .logs-checkbox-wrap {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
  }
  .logs-checkbox {
    inline-size: 16px;
    block-size: 16px;
    accent-color: rgb(var(--md-sys-color-primary));
    cursor: pointer;
  }

  /* Sentinel sits just above the manual Load-more button. 1px tall so it
     isn't visible but the IntersectionObserver still fires when it crosses
     the viewport edge (rootMargin extends the trigger zone). */
  .logs-sentinel {
    inline-size: 100%;
    block-size: 1px;
  }

  /* Floating selection toolbar pinned to the bottom-center of the viewport.
     M3-flavoured: filled surface, full-pill buttons, soft shadow. */
  .logs-selbar {
    position: fixed;
    inset-block-end: 1.5rem;
    inset-inline: 0;
    margin-inline: auto;
    inline-size: max-content;
    max-inline-size: calc(100vw - 2rem);
    z-index: 30;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.5rem 0.75rem;
    background: rgb(var(--md-sys-color-inverse-surface));
    color: rgb(var(--md-sys-color-inverse-on-surface));
    border-radius: 999px;
    box-shadow: 0 8px 24px rgb(0 0 0 / 0.18);
  }
  .logs-selbar-count {
    font-size: 13px;
    font-weight: 500;
    padding-inline: 0.5rem;
  }
  .logs-selbar-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.375rem 0.875rem;
    border-radius: 999px;
    background: transparent;
    color: inherit;
    border: 0;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
  }
  .logs-selbar-btn:hover { background: rgb(var(--md-sys-color-inverse-on-surface) / 0.12); }
  .logs-selbar-btn-text {
    display: inline-flex;
    align-items: center;
    inline-size: 32px;
    block-size: 32px;
    justify-content: center;
    border-radius: 999px;
    background: transparent;
    color: inherit;
    border: 0;
    cursor: pointer;
  }
  .logs-selbar-btn-text:hover { background: rgb(var(--md-sys-color-inverse-on-surface) / 0.12); }

  /* Mobile-bottom-nav avoidance: AppShell's mobile nav is fixed at bottom 0,
     so on small screens the selection bar needs to lift above it. */
  @media (max-width: 768px) {
    .logs-selbar { inset-block-end: 5rem; }
  }
</style>
